/**
 * Allocation results, shaped for the screens.
 *
 * Computed rather than replayed: `allocationEngine.ts` runs over the three
 * inputs the platform stores — the dairy roster, monthly production and station
 * capacity per period — and this module shapes the result into what the ledger
 * and lot-inventory screens render. The closure figures a verifier reads are
 * therefore genuine outputs of the algorithm rather than numbers asserted
 * alongside it, which is why a wrong ordering rule shows up as a broken closure
 * banner instead of hiding.
 *
 * Parameterised by its inputs, not bound to a fixture. The same code path runs
 * against `GET /lcfs/*` in the app and against the seed in tests and offline
 * mode, so what a test proves is what a user sees.
 *
 * Replaying every period from the beginning is the only correct way to allocate
 * one of them — a period's opening balance is the previous period's close — so
 * `buildAllocation` does the whole history once and the per-period views read
 * off it.
 */

import { Decimal } from "@compliance/domain";
import type {
  AllocationClosure,
  AllocationLedgerRow,
  AllocationPeriod,
  AllocationStationGroup,
  DairyDestinationRow,
  DairyHistory,
  DairyHistoryPeriod,
  DairyStationTotal,
  Figure,
  LotInventory,
  LotRow,
  StationClosureState,
  StationDairyTotal,
  StationHistory,
  StationHistoryPeriod,
} from "../api/types";
import { type AllocationResult, runAllocation, vintageAge } from "./allocationEngine";

/**
 * Tolerance for calling a figure closed.
 *
 * With the inputs at the source's own precision the worst station residual over
 * all 17 periods is 0.003 MMBtu — and that one is real, the January 2025 queue
 * running 0.003 short of what the stations claimed. A hundredth of an MMBtu
 * sits three times above that dust and six orders of magnitude below any
 * shortfall worth a verifier's attention, the largest being August 2025's
 * 1,654.547.
 */
const CLOSED = new Decimal("0.01");
const ZERO = new Decimal(0);

/** A dairy, reduced to what the allocation and its screens need. */
export type ViewDairy = {
  dairy_id: string;
  name: string;
  roster_position: number;
  fpc_code: string | null;
};

/** A station, reduced to the name a group header shows. */
export type ViewStation = { fse_id: string; station_name: string };

export type ViewProduction = {
  dairy_id: string;
  period: string;
  mmbtu: string;
  /** How the figure was arrived at — see {@link LotProvenance}. */
  source: "telemetry" | "workbook";
  /** Days of the month that leaned on a tier below the custody meter. */
  substituted_days: number;
};

/**
 * Where one lot's volume came from (BIO-381).
 *
 * A dairy-month's production is either transcribed from the customer's workbook
 * or derived from the meters — the injected energy at the PG&E custody point,
 * split by each dairy's own gathering-line meter and gas analyzer. A derived
 * figure may also have leaned on a lower tier for some days (the GC outlet flow,
 * or the plant's consolidated record) when the custody accumulator was dark.
 *
 * The backend discloses both so a verifier sees a substitution instead of having
 * to infer one, which only works if a screen says so. A lot is dairy × vintage,
 * so exactly one production row backs each one and the disclosure travels with
 * it unaggregated.
 */
export type LotProvenance = {
  source: "telemetry" | "workbook";
  substituted_days: number;
};

/** `dairyId|vintage` — the identity a lot and its production row share. */
function lotKey(dairyId: string, vintage: string): string {
  return `${dairyId}|${vintage}`;
}

/** What a lot with no matching production row reads as. */
const TRANSCRIBED: LotProvenance = { source: "workbook", substituted_days: 0 };

export type ViewCapacity = {
  fse_id: string;
  period: string;
  mmbtu: string;
  feed_order: number;
};

export type AllocationInputs = {
  dairies: readonly ViewDairy[];
  stations: readonly ViewStation[];
  production: readonly ViewProduction[];
  capacities: readonly ViewCapacity[];
  periods: readonly string[];
};

/** The whole history allocated once, plus the lookups the views read through. */
export type BuiltAllocation = {
  byPeriod: Map<string, AllocationResult>;
  dairyById: Map<string, ViewDairy>;
  stationNameById: Map<string, string>;
  capacitiesByPeriod: Map<string, readonly ViewCapacity[]>;
  /** `dairyId|vintage` -> provenance. Carried beside the engine's output rather
   * than through it: the engine is pure allocation arithmetic, and provenance
   * changes none of it. */
  provenanceByLot: Map<string, LotProvenance>;
  /**
   * Production per dairy, oldest period first — the supply-side series.
   *
   * The same rows `provenanceByLot` is built from, kept whole and indexed by
   * dairy rather than by lot. A dairy's history needs the *quantity* booked to
   * each month, which the engine's output cannot give back: a vintage that was
   * never drawn on leaves no allocation row, and a lot that drained completely
   * is dropped from `AllocationResult.lots`. Reading production off the
   * allocation would therefore report a productive month as a silent zero.
   */
  productionByDairy: Map<string, readonly ViewProduction[]>;
  periods: readonly string[];
  latestPeriod: string;
};

export function buildAllocation(inputs: AllocationInputs): BuiltAllocation {
  const dairyById = new Map(inputs.dairies.map((d) => [d.dairy_id, d]));
  // A dairy the roster does not know sorts last rather than first: an unknown
  // dairy must not silently take precedence in the draw order.
  const rosterPosition = (id: string) => dairyById.get(id)?.roster_position ?? 999;

  const capacitiesByPeriod = new Map<string, ViewCapacity[]>();
  for (const capacity of inputs.capacities) {
    const bucket = capacitiesByPeriod.get(capacity.period);
    if (bucket) bucket.push(capacity);
    else capacitiesByPeriod.set(capacity.period, [capacity]);
  }

  const stationNameById = new Map(inputs.stations.map((s) => [s.fse_id, s.station_name]));

  // Sorted once here rather than at each read: the wire order is the backend's
  // `ORDER BY`, and a history table that assumes oldest-first must not inherit
  // whatever order a future endpoint happens to return.
  const productionByDairy = new Map<string, ViewProduction[]>();
  for (const row of inputs.production) {
    const bucket = productionByDairy.get(row.dairy_id);
    if (bucket) bucket.push(row);
    else productionByDairy.set(row.dairy_id, [row]);
  }
  for (const bucket of productionByDairy.values()) {
    bucket.sort((a, b) => (a.period < b.period ? -1 : a.period > b.period ? 1 : 0));
  }

  const byPeriod = runAllocation({
    periods: [...inputs.periods],
    production: inputs.production.map((p) => ({
      dairyId: p.dairy_id,
      month: p.period,
      mmbtu: new Decimal(p.mmbtu),
    })),
    capacities: inputs.capacities.map((c) => ({
      period: c.period,
      aemetisFseId: c.fse_id,
      // The engine stamps the name onto each row it emits. Resolved here rather
      // than carried on the capacity row: the name belongs to the station, and
      // repeating it on all 99 period rows is what the backend stopped doing.
      station: stationNameById.get(c.fse_id) ?? c.fse_id,
      mmbtu: new Decimal(c.mmbtu),
      feedOrder: c.feed_order,
    })),
    rosterPosition,
  });

  return {
    byPeriod,
    dairyById,
    stationNameById,
    capacitiesByPeriod,
    provenanceByLot: new Map(
      inputs.production.map((p) => [
        lotKey(p.dairy_id, p.period),
        { source: p.source, substituted_days: p.substituted_days },
      ]),
    ),
    productionByDairy,
    periods: inputs.periods,
    latestPeriod: inputs.periods[inputs.periods.length - 1] ?? "",
  };
}

// Three decimals, matching what the source stores: station capacity is entered
// to 3dp and production is a full-precision computed column. Cutting the wire
// at 2dp would put that precision back where it was.
const mmbtu = (d: Decimal) => ({ value: d.toFixed(3), basis: "mmbtu" as const });

function closureFor(result: AllocationResult): AllocationClosure {
  const unexplained = result.stationResiduals.filter(
    (s) => s.residual.abs().gt(CLOSED) && !result.queueExhausted,
  );
  const residualTotal = result.stationResiduals.reduce(
    (acc, s) => acc.plus(s.residual.abs()),
    ZERO,
  );

  let station: StationClosureState = "ok";
  if (unexplained.length > 0) station = "residual";
  else if (result.queueExhausted && residualTotal.gt(CLOSED)) station = "queue_exhausted";

  return {
    station_closure: station,
    target_closure: result.allocated.minus(result.capacity).abs().lte(CLOSED) ? "ok" : "mismatch",
    unexplained_stations: unexplained.length,
    residual_total: mmbtu(residualTotal),
  };
}

/** The ledger for one period, or `null` if the period was never allocated. */
export function allocationPeriodView(
  built: BuiltAllocation,
  period: string,
): AllocationPeriod | null {
  const result = built.byPeriod.get(period);
  if (!result) return null;

  const residualByStation = new Map(
    result.stationResiduals.map((s) => [s.aemetisFseId, s.residual]),
  );

  const groups = new Map<string, AllocationStationGroup>();
  for (const capacity of built.capacitiesByPeriod.get(period) ?? []) {
    groups.set(capacity.fse_id, {
      // Falls back to the id rather than an empty cell: a station missing from
      // the roster is a data problem a reader should be able to see and chase.
      station: built.stationNameById.get(capacity.fse_id) ?? capacity.fse_id,
      aemetis_fse_id: capacity.fse_id,
      feed_order: capacity.feed_order,
      capacity: mmbtu(new Decimal(capacity.mmbtu)),
      residual: mmbtu(residualByStation.get(capacity.fse_id) ?? ZERO),
      rows: [],
    });
  }

  for (const row of result.rows) {
    const group = groups.get(row.aemetisFseId);
    if (!group) continue;
    const dairy = built.dairyById.get(row.dairyId);
    group.rows.push({
      dairy_id: row.dairyId,
      dairy_name: dairy?.name ?? row.dairyId,
      vintage_month: row.vintage,
      age_periods: vintageAge(row.vintage, period),
      mmbtu: mmbtu(row.mmbtu),
      fpc_code: dairy?.fpc_code ?? "",
    });
  }

  return {
    period,
    allocated: mmbtu(result.allocated),
    capacity: mmbtu(result.capacity),
    banked: mmbtu(result.banked),
    queue_exhausted: result.queueExhausted,
    row_count: result.rows.length,
    lots_touched: new Set(result.rows.map((r) => `${r.dairyId}|${r.vintage}`)).size,
    closure: closureFor(result),
    // Feed order, because that is the order the allocation consumed them in —
    // any other sort makes the arithmetic look arbitrary.
    stations: [...groups.values()].sort((a, b) => a.feed_order - b.feed_order),
    available_periods: [...built.periods],
    computed_at: new Date().toISOString(),
  };
}

/** The lot bank as of one period, or `null` if it was never allocated. */
export function lotInventoryView(built: BuiltAllocation, period: string): LotInventory | null {
  const result = built.byPeriod.get(period);
  if (!result) return null;

  const rosterPosition = (id: string) => built.dairyById.get(id)?.roster_position ?? 999;

  const items: LotRow[] = result.lots
    .map((lot) => {
      const dairy = built.dairyById.get(lot.dairyId);
      return {
        dairy_id: lot.dairyId,
        dairy_name: dairy?.name ?? lot.dairyId,
        fpc_code: dairy?.fpc_code ?? "",
        vintage_month: lot.vintage,
        age_periods: vintageAge(lot.vintage, period),
        produced: mmbtu(lot.produced),
        opening: mmbtu(lot.opening),
        consumed: mmbtu(lot.consumed),
        remaining: mmbtu(lot.remaining),
        // A lot with no production row is a transcribed figure by construction:
        // the derived source is the only thing that stamps provenance.
        ...(built.provenanceByLot.get(lotKey(lot.dairyId, lot.vintage)) ?? TRANSCRIBED),
      };
    })
    .sort(
      (a, b) =>
        b.age_periods - a.age_periods || rosterPosition(a.dairy_id) - rosterPosition(b.dairy_id),
    );

  const banked = result.lots.reduce((acc, l) => acc.plus(l.remaining), ZERO);
  // Items are already oldest-first, so the first one still holding volume IS
  // the oldest banked lot. Reporting its vintage as well as its age is what
  // lets the screen distinguish "nothing banked" from "banked, all current
  // vintage" — both are age zero.
  const oldestBanked = items.find((i) => new Decimal(i.remaining.value).gt("0.005"));

  return {
    period,
    items,
    banked_total: mmbtu(banked),
    oldest_age_periods: oldestBanked?.age_periods ?? 0,
    oldest_banked_vintage: oldestBanked?.vintage_month ?? null,
    available_periods: [...built.periods],
    computed_at: new Date().toISOString(),
  };
}

// ── Drill-downs: one station, or one dairy, across every period ────────────
//
// The ledger and the lot inventory slice the same allocation by period. These
// two slice it by party. Nothing is recomputed and nothing is fetched:
// `buildAllocation` has already replayed all seventeen periods, so both of
// these are a walk over `byPeriod` filtered to one id.

/** Descending by MMBtu — a lifetime total is read largest-first. */
function byMmbtuDesc<T extends { mmbtu: Figure }>(a: T, b: T): number {
  return new Decimal(b.mmbtu.value).comparedTo(new Decimal(a.mmbtu.value));
}

/**
 * One station's allocation history, newest period first.
 *
 * Returns `null` only for an id the roster has never heard of, which is a
 * broken link rather than a station with no volume. A registered station that
 * never claimed capacity gets a real record with an empty `periods` — eleven of
 * the forty-five are in that position, and the two cases must not look alike.
 */
export function stationHistoryView(built: BuiltAllocation, fseId: string): StationHistory | null {
  const stationName = built.stationNameById.get(fseId);
  if (stationName === undefined) return null;

  const periods: StationHistoryPeriod[] = [];
  let totalCapacity = ZERO;
  let totalAllocated = ZERO;
  const dairyTotals = new Map<string, { row: StationDairyTotal; total: Decimal }>();

  for (const period of built.periods) {
    const result = built.byPeriod.get(period);
    if (!result) continue;

    const claim = (built.capacitiesByPeriod.get(period) ?? []).find((c) => c.fse_id === fseId);
    const rows = result.rows.filter((r) => r.aemetisFseId === fseId);
    // A period this station took no part in is left out rather than zeroed. A
    // station that joined the feed in 2025-06 did not dispense nothing in
    // 2025-05; it was not there.
    if (!claim && rows.length === 0) continue;

    const allocated = rows.reduce((acc, r) => acc.plus(r.mmbtu), ZERO);
    const capacity = claim ? new Decimal(claim.mmbtu) : ZERO;
    totalCapacity = totalCapacity.plus(capacity);
    totalAllocated = totalAllocated.plus(allocated);

    const sources: AllocationLedgerRow[] = rows.map((row) => {
      const dairy = built.dairyById.get(row.dairyId);
      const name = dairy?.name ?? row.dairyId;
      const fpc = dairy?.fpc_code ?? "";

      const seen = dairyTotals.get(row.dairyId);
      if (seen) seen.total = seen.total.plus(row.mmbtu);
      else {
        dairyTotals.set(row.dairyId, {
          row: { dairy_id: row.dairyId, dairy_name: name, fpc_code: fpc, mmbtu: mmbtu(ZERO) },
          total: row.mmbtu,
        });
      }

      return {
        dairy_id: row.dairyId,
        dairy_name: name,
        vintage_month: row.vintage,
        age_periods: vintageAge(row.vintage, period),
        mmbtu: mmbtu(row.mmbtu),
        fpc_code: fpc,
      };
    });

    periods.push({
      period,
      // Zero rather than a guess when volume arrived without a claim: the feed
      // order is a property of the capacity row, and inventing one would put
      // this station in a draw position it never held.
      feed_order: claim?.feed_order ?? 0,
      capacity: mmbtu(capacity),
      allocated: mmbtu(allocated),
      residual: mmbtu(
        result.stationResiduals.find((s) => s.aemetisFseId === fseId)?.residual ?? ZERO,
      ),
      queue_exhausted: result.queueExhausted,
      sources,
    });
  }

  const first = periods[0]?.period ?? null;
  const last = periods[periods.length - 1]?.period ?? null;

  return {
    fse_id: fseId,
    station_name: stationName,
    periods: [...periods].reverse(),
    total_capacity: mmbtu(totalCapacity),
    total_allocated: mmbtu(totalAllocated),
    by_dairy: [...dairyTotals.values()]
      .map((d) => ({ ...d.row, mmbtu: mmbtu(d.total) }))
      .sort(byMmbtuDesc),
    first_period: first,
    last_period: last,
    computed_at: new Date().toISOString(),
  };
}

/**
 * One dairy's production and sending history, newest period first.
 *
 * `null` for a dairy the roster does not carry. A rostered dairy that has never
 * produced — four of the sixteen, the ones with no facility — gets a real record
 * with an empty `periods`.
 *
 * The balance columns come from the engine's own lots rather than being summed
 * here, so `opening + produced − dispensed === banked` is a statement about the
 * allocation and not about this function's arithmetic. `lot.opening` is snapshot
 * *after* this period's production is merged, which is why `produced` is
 * subtracted back out to get the balance the dairy actually carried in.
 */
export function dairyHistoryView(built: BuiltAllocation, dairyId: string): DairyHistory | null {
  const dairy = built.dairyById.get(dairyId);
  if (!dairy) return null;

  const producedByPeriod = new Map(
    (built.productionByDairy.get(dairyId) ?? []).map((p) => [p.period, p]),
  );

  const periods: DairyHistoryPeriod[] = [];
  let totalProduced = ZERO;
  let totalDispensed = ZERO;
  let bankedNow = ZERO;
  const stationTotals = new Map<string, { row: DairyStationTotal; total: Decimal }>();

  for (const period of built.periods) {
    const result = built.byPeriod.get(period);
    if (!result) continue;

    const lots = result.lots.filter((l) => l.dairyId === dairyId);
    const booked = producedByPeriod.get(period);
    // Nothing produced, nothing banked, nothing dispensed — the dairy simply
    // was not part of this period. Skipping keeps the series to its real span
    // instead of padding both ends with zeros.
    if (lots.length === 0 && !booked) continue;

    const produced = booked ? new Decimal(booked.mmbtu) : ZERO;
    const dispensed = lots.reduce((acc, l) => acc.plus(l.consumed), ZERO);
    const banked = lots.reduce((acc, l) => acc.plus(l.remaining), ZERO);
    const opening = lots.reduce((acc, l) => acc.plus(l.opening), ZERO).minus(produced);

    totalProduced = totalProduced.plus(produced);
    totalDispensed = totalDispensed.plus(dispensed);
    bankedNow = banked;

    const destinations: DairyDestinationRow[] = result.rows
      .filter((r) => r.dairyId === dairyId)
      .map((row) => {
        const name = built.stationNameById.get(row.aemetisFseId) ?? row.aemetisFseId;
        const seen = stationTotals.get(row.aemetisFseId);
        if (seen) seen.total = seen.total.plus(row.mmbtu);
        else {
          stationTotals.set(row.aemetisFseId, {
            row: { fse_id: row.aemetisFseId, station_name: name, mmbtu: mmbtu(ZERO) },
            total: row.mmbtu,
          });
        }
        return {
          fse_id: row.aemetisFseId,
          station_name: name,
          vintage_month: row.vintage,
          age_periods: vintageAge(row.vintage, period),
          mmbtu: mmbtu(row.mmbtu),
        };
      });

    periods.push({
      period,
      produced: mmbtu(produced),
      source: booked?.source ?? null,
      substituted_days: booked?.substituted_days ?? null,
      opening: mmbtu(opening),
      dispensed: mmbtu(dispensed),
      banked: mmbtu(banked),
      destinations,
    });
  }

  return {
    dairy_id: dairyId,
    dairy_name: dairy.name,
    fpc_code: dairy.fpc_code ?? "",
    roster_position: dairy.roster_position,
    periods: [...periods].reverse(),
    total_produced: mmbtu(totalProduced),
    total_dispensed: mmbtu(totalDispensed),
    // The last period that had any activity, not the last period on the
    // calendar — they are the same whenever the dairy is still active, and when
    // it is not, its closing balance is the one it stopped at.
    banked_now: mmbtu(bankedNow),
    by_station: [...stationTotals.values()]
      .map((s) => ({ ...s.row, mmbtu: mmbtu(s.total) }))
      .sort(byMmbtuDesc),
    first_production_period: producedByPeriod.size > 0 ? [...producedByPeriod.keys()][0] : null,
    computed_at: new Date().toISOString(),
  };
}
